/**
 * SideMenuItemArray - Light Theme Menu Items
 * 
 * Eye-friendly design with:
 * - Soft hover backgrounds
 * - Muted text colors
 * - Subtle active indicators
 */

import React, { useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import { useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { Collapse } from 'react-collapse';
import * as RiIcons from 'react-icons/ri';
import '../styles/ModernStyles.css';

export default function SideMenuItemArray({ open, setOpen, pathsList }) {
  const location = useLocation();
  const [openCollapse, setOpenCollapse] = useState(false);

  const openCollapseToggle = () => {
    setOpenCollapse(!openCollapse);
  };

  return (
    <List
      sx={{
        height: '100% !important',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: open ? 'flex-start' : 'center',
        justifyContent: 'start',
        padding: '0 8px',
        gap: 0.5,

        // Light theme icon style
        '& .iconSideMenu': {
          color: '#64748B',
          fontSize: '22px',
          transition: 'all 0.3s ease',
        },

        // Text style - Light theme
        '& .MuiListItemText-root .MuiTypography-root': {
          textAlign: 'left',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'Cairo, sans-serif',
          letterSpacing: '0px',
          color: '#475569',
          transition: 'all 0.3s ease',
        },

        // Button base
        '& .MuiButtonBase-root': {
          backgroundColor: 'transparent !important',
          padding: '8px 16px !important',
          borderRadius: '10px',
        },

        // List item
        '& .MuiListItem-root': {
          borderRadius: '10px',
          width: open ? '100%' : '44px',
          height: '44px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          margin: '0 auto',
          transition: 'all 0.3s ease',
        },

        // Hover effects - Light theme
        '& .MuiListItem-root:hover': {
          backgroundColor: 'rgba(78, 205, 196, 0.08) !important',
        },
        '& .MuiListItem-root:hover .iconSideMenu': {
          color: '#4ECDC4 !important',
        },
        '& .MuiListItem-root:hover .MuiListItemText-root .MuiTypography-root': {
          color: '#4ECDC4 !important',
        },

        // Active state - Light theme
        '& .active .MuiListItem-root': {
          backgroundColor: 'rgba(78, 205, 196, 0.1) !important',
          borderLeft: '3px solid #4ECDC4',
        },
        '& .active .iconSideMenu': {
          color: '#4ECDC4 !important',
        },
        '& .active .MuiListItemText-root .MuiTypography-root': {
          color: '#4ECDC4 !important',
          fontWeight: 600,
        },
      }}
    >
      {pathsList && pathsList?.length > 0 && pathsList?.map((obj, index) => (
        <React.Fragment key={obj?.name + '_' + obj?.id}>
          {obj && obj?.subLink?.length === 0 ? (
            <NavLink
              to={obj?.to}
              onClick={() => setOpen(false)}
              style={{ width: '100%', textDecoration: 'none' }}
            >
              <ListItem disablePadding sx={{ display: 'block', mt: 0 }} disableRipple>
                <ListItemButton
                  disableRipple
                  sx={{
                    minHeight: 44,
                    justifyContent: open ? 'initial' : 'center',
                    px: 2,
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 2 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {obj?.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={obj?.name}
                    sx={{
                      opacity: open ? 1 : 0,
                      transition: 'opacity 0.3s ease',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </NavLink>
          ) : obj ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: '10px 16px',
                borderRadius: '10px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(78, 205, 196, 0.08)',
                },
              }}
              onClick={openCollapseToggle}
            >
              <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 2 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                  }}
                >
                  {obj?.icon}
                </ListItemIcon>
                <ListItemText
                  primary={obj?.name}
                  sx={{ opacity: open ? 1 : 0 }}
                />
              </Box>
              {open && (
                openCollapse ? (
                  <RiIcons.RiArrowDropUpLine
                    style={{ fontSize: '22px', color: '#4ECDC4' }}
                  />
                ) : (
                  <RiIcons.RiArrowDropDownLine
                    style={{ fontSize: '22px', color: '#94A3B8' }}
                  />
                )
              )}
            </Box>
          ) : null}

          {obj && obj?.subLink?.length > 0 && (
            <Collapse isOpened={openCollapse && open}>
              <Box sx={{ pl: 2 }}>
                {obj?.subLink?.map((sub) => (
                  <NavLink
                    to={sub?.to}
                    key={sub?.name + '_' + sub?.id}
                    style={{ textDecoration: 'none', width: '100%' }}
                  >
                    <ListItem disablePadding sx={{ display: 'block', mt: 0 }} disableRipple>
                      <ListItemButton
                        disableRipple
                        sx={{
                          minHeight: 40,
                          justifyContent: open ? 'initial' : 'center',
                          px: 2,
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 2 : 'auto',
                            justifyContent: 'center',
                          }}
                        >
                          {sub?.icon}
                        </ListItemIcon>
                        <ListItemText primary={sub?.name} sx={{ opacity: open ? 1 : 0 }} />
                      </ListItemButton>
                    </ListItem>
                  </NavLink>
                ))}
              </Box>
            </Collapse>
          )}
        </React.Fragment>
      ))}
    </List>
  );
}
